/**
 * Payment Slice
 *
 * Handles bulk payment fetches via POST /payments/fetch.
 * - fetchBulkPayments: fetches up to 100 payments (for BulkPaymentsPage)
 *
 * The single-payment detail page (/payment) no longer uses this slice — it is
 * served by the paymentDetail slice (store/slices/paymentDetail).
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { paymentInitialState, FetchPaymentsResponse } from './paymentState';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';

/** Fetches multiple payments by IDs. Stores result in state.bulkData. */
export const fetchBulkPayments = createAsyncThunk<
  FetchPaymentsResponse, string[], { state: RootState; rejectValue: string }
>('payment/fetchBulk', async (paymentIds, { getState, rejectWithValue }) => {
  const { apiBase } = getState().env;
  try {
    return await beaconApi.fetchPayments(paymentIds, apiBase);
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch payments');
  }
});

const paymentSlice = createSlice({
  name: 'payment',
  initialState: paymentInitialState,
  reducers: {
    clearBulkPayments: (state) => {
      state.bulkData = null;
      state.bulkLoading = false;
      state.bulkError = null;
      state.bulkRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBulkPayments.pending, (state, action) => {
        state.bulkLoading = true;
        state.bulkError = null;
        state.bulkRequestId = action.meta.requestId;
      })
      .addCase(fetchBulkPayments.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.bulkRequestId) return;
        state.bulkLoading = false;
        state.bulkData = action.payload;
        state.bulkError = null;
      })
      .addCase(fetchBulkPayments.rejected, (state, action) => {
        if (action.meta.requestId !== state.bulkRequestId) return;
        state.bulkLoading = false;
        state.bulkError = action.payload || action.error.message || 'Failed to fetch payments';
      });
  },
});

export const { clearBulkPayments } = paymentSlice.actions;
export default paymentSlice.reducer;
